import { Dayjs } from 'dayjs';
import { IEffortRow } from './effort.types';
import { ITeamRecoveryRow } from './useTeamRecovery';

// ─────────────────────────────────────────────────────────────────────────────
// Join puro Sforzo↔Recovery per la vista "Sforzo + Recovery" (cella bicolore).
// Sforzo: chiave athlete._id, giorni allineati per DATA (day.date).
// Recovery: chiave athlete.id, giorni allineati per INDICE (days[i]).
// ─────────────────────────────────────────────────────────────────────────────

/** Atleta della riga combinata — id unificato (athlete._id ↔ athlete.id) */
export interface ICombinedAthlete {
  id: string;
  firstName: string;
  lastName: string;
  role?: string;
}

/** Cella-giorno combinata: sRPE (sforzo) e RI (recovery), entrambi opzionali */
export interface ICombinedDayCell {
  /** YYYY-MM-DD */
  date: string;
  srpe: number | null;
  ri: number | null;
}

export interface ICombinedAthleteRow {
  athlete: ICombinedAthlete;
  cells: ICombinedDayCell[];
}

/**
 * Unisce le righe Sforzo e Recovery sulla stessa settimana `weekDays`.
 * Un atleta presente in una sola fonte produce comunque una riga: la metà
 * mancante resta `null` su ogni cella (gestita a livello di rendering cella).
 */
export function joinEffortRecoveryRows(
  effortRows: IEffortRow[],
  recoveryRows: ITeamRecoveryRow[],
  weekDays: Dayjs[],
): ICombinedAthleteRow[] {
  const recoveryById = new Map(recoveryRows.map((r) => [r.athlete.id, r]));
  const seen = new Set<string>();

  const rows = effortRows.map((effortRow) => {
    const id = effortRow.athlete._id;
    seen.add(id);
    return buildCombinedRow(id, effortRow, recoveryById.get(id), weekDays);
  });

  // Atleti presenti solo in Recovery (assenti dalla risposta Sforzo)
  const recoveryOnlyRows = recoveryRows
    .filter((r) => !seen.has(r.athlete.id))
    .map((recoveryRow) => buildCombinedRow(recoveryRow.athlete.id, undefined, recoveryRow, weekDays));

  return [...rows, ...recoveryOnlyRows];
}

function buildCombinedRow(
  id: string,
  effortRow: IEffortRow | undefined,
  recoveryRow: ITeamRecoveryRow | undefined,
  weekDays: Dayjs[],
): ICombinedAthleteRow {
  const athlete: ICombinedAthlete = {
    id,
    firstName: effortRow?.athlete.firstName ?? recoveryRow?.athlete.firstName ?? '',
    lastName: effortRow?.athlete.lastName ?? recoveryRow?.athlete.lastName ?? '',
    role: effortRow?.athlete.role ?? recoveryRow?.athlete.position,
  };

  const cells: ICombinedDayCell[] = weekDays.map((day, i) => {
    const date = day.format('YYYY-MM-DD');
    const srpe = effortRow?.days.find((d) => d.date === date)?.srpe ?? null;
    const ri = recoveryRow?.days[i] ?? null;
    return { date, srpe, ri };
  });

  return { athlete, cells };
}
